import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Loader2, Mail, ShieldAlert, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Shared, surface-agnostic profile form used by both the admin SPA (/profile)
 * and the partner portal SPA (/portal/profile). Two cards: personal info
 * (full name + email) and change password. Email change opens a confirmation
 * dialog before submit since it revokes other active sessions server-side.
 *
 * The form is dumb about which surface it's on — the caller wires the
 * orval-generated mutations and passes them as `onSavePersonal` /
 * `onChangePassword`. That keeps this component free of API-client knowledge
 * so it stays cheap to test in isolation.
 */
export interface ProfileFormProps {
  /** Initial values from GET /me. Re-syncs when the values change. */
  initial: {
    email: string;
    fullName: string | null | undefined;
    role: string;
  };
  /** Whether the surface allows email editing. Defaults true; pages can set false to lock it down. */
  allowEmailEdit?: boolean;
  /** Whether saving personal info is in flight. */
  isSavingPersonal?: boolean;
  /** Whether changing password is in flight. */
  isChangingPassword?: boolean;
  /**
   * Persist personal info. The caller is responsible for the actual mutation;
   * resolve to trigger the success state, reject (throw) to surface the toast.
   * `kind` distinguishes name-only from email-changing changes so the caller
   * can show a session-revocation toast on email.
   */
  onSavePersonal: (next: {
    fullName: string;
    email: string;
    kind: 'name-only' | 'email-changed';
  }) => Promise<void> | void;
  /** Persist a password change. Same error/success contract as onSavePersonal. */
  onChangePassword: (next: { currentPassword: string; newPassword: string }) => Promise<void> | void;
}

const PersonalSchema = z.object({
  fullName: z.string().trim().min(1, 'Full name is required.').max(200, 'Max 200 chars.'),
  email: z
    .string()
    .trim()
    .min(1, 'Email is required.')
    .email('Enter a valid email address.')
    .max(320),
});

type PersonalValues = z.infer<typeof PersonalSchema>;

const PasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required.'),
    newPassword: z.string().min(12, 'New password must be at least 12 characters.'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  })
  .refine((data) => data.newPassword !== data.currentPassword, {
    message: 'New password must differ from current.',
    path: ['newPassword'],
  });

type PasswordValues = z.infer<typeof PasswordSchema>;

export function ProfileForm({
  initial,
  allowEmailEdit = true,
  isSavingPersonal = false,
  isChangingPassword = false,
  onSavePersonal,
  onChangePassword,
}: ProfileFormProps) {
  return (
    <div className="space-y-6">
      <PersonalCard
        initial={initial}
        allowEmailEdit={allowEmailEdit}
        isSaving={isSavingPersonal}
        onSave={onSavePersonal}
      />
      <PasswordCard
        isSaving={isChangingPassword}
        onSave={onChangePassword}
      />
      <Card className="border-amber-300/40 bg-amber-50/40 dark:border-amber-400/30 dark:bg-amber-900/20">
        <CardContent className="flex items-start gap-3 p-4 text-xs text-amber-900 dark:text-amber-200">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Changing your email or password will sign you out of every other
            active session. Your current device stays signed in.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PersonalCard({
  initial,
  allowEmailEdit,
  isSaving,
  onSave,
}: {
  initial: ProfileFormProps['initial'];
  allowEmailEdit: boolean;
  isSaving: boolean;
  onSave: ProfileFormProps['onSavePersonal'];
}) {
  const form = useForm<PersonalValues>({
    resolver: zodResolver(PersonalSchema),
    defaultValues: {
      fullName: initial.fullName ?? '',
      email: initial.email,
    },
  });

  // Re-sync if the parent re-fetches /me after a successful save.
  useEffect(() => {
    form.reset({ fullName: initial.fullName ?? '', email: initial.email });
    // We intentionally depend on the primitive values so changing the object
    // identity alone doesn't trigger a reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.fullName, initial.email]);

  const [pendingEmailConfirm, setPendingEmailConfirm] = useState<PersonalValues | null>(null);

  const submit = form.handleSubmit(async (values) => {
    const emailChanged = values.email.trim().toLowerCase() !== initial.email.toLowerCase();
    if (emailChanged) {
      setPendingEmailConfirm(values);
      return;
    }
    try {
      await onSave({ fullName: values.fullName.trim(), email: initial.email, kind: 'name-only' });
      toast.success('Profile updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save profile.');
    }
  });

  const confirmEmailChange = async () => {
    if (!pendingEmailConfirm) return;
    try {
      await onSave({
        fullName: pendingEmailConfirm.fullName.trim(),
        email: pendingEmailConfirm.email.trim().toLowerCase(),
        kind: 'email-changed',
      });
      toast.success('Email updated. Other sessions have been signed out.');
      setPendingEmailConfirm(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change email.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <User className="h-4 w-4 text-muted-foreground" />
          Personal info
        </CardTitle>
        <CardDescription>
          Your display name and login email. {!allowEmailEdit && 'Email is read-only on this account.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="fullName">Full name</Label>
            <Input id="fullName" autoComplete="name" {...form.register('fullName')} />
            {form.formState.errors.fullName ? (
              <p className="text-xs text-rose-600">{form.formState.errors.fullName.message}</p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              disabled={!allowEmailEdit}
              {...form.register('email')}
            />
            {form.formState.errors.email ? (
              <p className="text-xs text-rose-600">{form.formState.errors.email.message}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button type="submit" disabled={isSaving || !form.formState.isDirty}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
            <div className="text-[11px] text-muted-foreground">
              Role: <span className="font-mono">{initial.role}</span>
            </div>
          </div>
        </form>
      </CardContent>

      <Dialog open={pendingEmailConfirm !== null} onOpenChange={(open) => !open && setPendingEmailConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Confirm email change
            </DialogTitle>
            <DialogDescription>
              You are about to change your login email from{' '}
              <span className="font-mono">{initial.email}</span> to{' '}
              <span className="font-mono">{pendingEmailConfirm?.email}</span>.
              <br />
              <span className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 p-2 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Every other active session for your account will be signed
                  out. Make sure you control the new address — there is no
                  email-link verification in this flow.
                </span>
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingEmailConfirm(null)}>
              Cancel
            </Button>
            <Button onClick={confirmEmailChange} disabled={isSaving}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirm change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PasswordCard({
  isSaving,
  onSave,
}: {
  isSaving: boolean;
  onSave: ProfileFormProps['onChangePassword'];
}) {
  const form = useForm<PasswordValues>({
    resolver: zodResolver(PasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await onSave({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success('Password changed. Other sessions have been signed out.');
      form.reset({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change password.');
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Change password</CardTitle>
        <CardDescription>
          Use at least 12 characters. We re-verify your current password before
          accepting the change.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="currentPassword">Current password</Label>
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              {...form.register('currentPassword')}
            />
            {form.formState.errors.currentPassword ? (
              <p className="text-xs text-rose-600">{form.formState.errors.currentPassword.message}</p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="newPassword">New password</Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              {...form.register('newPassword')}
            />
            {form.formState.errors.newPassword ? (
              <p className="text-xs text-rose-600">{form.formState.errors.newPassword.message}</p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              {...form.register('confirmPassword')}
            />
            {form.formState.errors.confirmPassword ? (
              <p className="text-xs text-rose-600">{form.formState.errors.confirmPassword.message}</p>
            ) : null}
          </div>
          <div className="pt-2">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Change password
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
